import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Plus, ShoppingBag, Minus, CreditCard, Users, Heart, Receipt } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiBase } from '../../lib/api';
import { getPublicImageUrl } from '../../lib/media';

const API = getApiBase();

const TIP_FIXED = [1, 2, 5];
const TIP_PERCENT = [5, 10, 15, 20];

export default function TableOrderPage() {
  const { qrCodeId } = useParams();
  const [table, setTable] = useState(null);
  const [categories, setCategories] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [activeCategory, setActiveCategory] = useState('');
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState('');
  const [formulaDish, setFormulaDish] = useState(null);
  const [formulaSelections, setFormulaSelections] = useState({});
  const { items, addItem, removeItem, updateQuantity, total, itemCount, setTableInfo, setOrderType, clearCart } = useCart();
  const [customerName, setCustomerName] = useState('');
  const [showCart, setShowCart] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('order'); // 'order' or 'pay'
  const [existingOrders, setExistingOrders] = useState([]);
  const [tip, setTip] = useState(0);
  const [tipType, setTipType] = useState('fixed');
  const [splitCount, setSplitCount] = useState(1);
  const [showSplit, setShowSplit] = useState(false);
  const [billSummary, setBillSummary] = useState({ subtotal: 0, paid_bill_amount: 0, remaining_total: 0, payment_complete: false });
  const navigate = useNavigate();

  useEffect(() => {
    setOrderType('dine_in');
    axios.get(`${API}/api/tables/qr/${qrCodeId}`).then(r => {
      setTable(r.data);
      setTableInfo(r.data);
    }).catch(() => setError('Table non trouvee'));
    Promise.all([
      axios.get(`${API}/api/settings`),
      axios.get(`${API}/api/categories`),
      axios.get(`${API}/api/dishes?available_only=true`)
    ]).then(([settingsRes, c, d]) => { setSettings(settingsRes.data); setCategories(c.data); setDishes(d.data); }).catch(() => {});
  }, [qrCodeId, setTableInfo, setOrderType]);

  const loadOrders = useCallback(async () => {
    if (!qrCodeId) return;
    try {
      const { data } = await axios.get(`${API}/api/orders/table-bill/${qrCodeId}`);
      setTable(currentTable => {
        const nextTable = data.table || currentTable;
        if (nextTable) {
          setTableInfo(nextTable);
        }
        return nextTable;
      });
      setExistingOrders(data.orders || []);
      setBillSummary({
        subtotal: data.subtotal || 0,
        paid_bill_amount: data.paid_bill_amount || 0,
        remaining_total: data.remaining_total || 0,
        payment_complete: !!data.payment_complete,
      });
    } catch {}
  }, [qrCodeId, setTableInfo]);

  useEffect(() => {
    if (table && mode === 'pay') loadOrders();
  }, [table, mode, loadOrders]);

  const visibleCategories = categories.filter(c => c.active !== false && c.visible_dine_in !== false);
  const filtered = dishes.filter(d => !activeCategory || d.category_id === activeCategory)
    .filter(d => visibleCategories.some(category => category.id === d.category_id));

  useEffect(() => {
    if (!visibleCategories.length) return;
    const stillExists = visibleCategories.some(category => category.id === activeCategory);
    if (!stillExists) {
      setActiveCategory(visibleCategories[0].id);
    }
  }, [visibleCategories, activeCategory]);

  const getDishesForGroup = (group) => {
    const explicitDishIds = new Set(group.source_dish_ids || []);
    if (explicitDishIds.size > 0) {
      return dishes.filter(d => explicitDishIds.has(d.id) && d.available);
    }
    return dishes.filter(d => group.source_category_ids?.includes(d.category_id) && d.available);
  };

  const getFormulaChoiceCount = (groupId, optionId) => (
    (formulaSelections[groupId] || []).filter(item => item.id === optionId).length
  );

  const getIncludedSelectionCount = (group) => Math.max(1, Number(group.selection_max) || 1);

  const getOptionConfiguredExtra = (group, option) => Number((group.extra_prices || {})[option.id] || 0);

  const getSelectionCharge = (group, item, selectionIndex) => {
    if (selectionIndex < getIncludedSelectionCount(group)) {
      return getOptionConfiguredExtra(group, item);
    }
    return Number(item.prices?.dine_in || 0);
  };

  const getGroupSurchargeTotal = (group) => (
    (formulaSelections[group.id] || []).reduce(
      (sum, item, index) => sum + getSelectionCharge(group, item, index),
      0
    )
  );

  const getOptionExtraCount = (group, optionId) => (
    (formulaSelections[group.id] || []).reduce((count, item, index) => {
      if (item.id === optionId && index >= getIncludedSelectionCount(group)) {
        return count + 1;
      }
      return count;
    }, 0)
  );

  const addFormulaChoice = (group, option) => {
    setFormulaSelections(prev => {
      const selected = prev[group.id] || [];
      if (selected.length >= 20) {
        toast.error(`Maximum 20 choix pour ${group.name}`);
        return prev;
      }
      return { ...prev, [group.id]: [...selected, option] };
    });
  };

  const removeFormulaChoice = (group, option) => {
    setFormulaSelections(prev => {
      const selected = prev[group.id] || [];
      const removeIndex = selected.findIndex(item => item.id === option.id);
      if (removeIndex === -1) return prev;
      return {
        ...prev,
        [group.id]: selected.filter((_, index) => index !== removeIndex),
      };
    });
  };

  const openFormula = (dish) => {
    if (settings?.dine_in_enabled === false) {
      toast.error('Le service sur place est temporairement indisponible');
      return;
    }
    if (settings?.exceptional_closure_enabled) {
      toast.error(settings.exceptional_closure_message || 'Le restaurant est temporairement ferme');
      return;
    }
    const emptyGroup = (dish.formula_groups || []).find(group => getDishesForGroup(group).length === 0);
    if (emptyGroup) {
      toast.error(`La formule "${dish.name}" n'est pas encore configuree: aucun choix disponible pour "${emptyGroup.name}"`);
      return;
    }
    const initialSelections = {};
    (dish.formula_groups || []).forEach(group => {
      initialSelections[group.id] = [];
    });
    setFormulaSelections(initialSelections);
    setFormulaDish(dish);
  };

  const confirmFormula = () => {
    if (!formulaDish) return;
    const invalidGroup = formulaDish.formula_groups.find(group => {
      const count = (formulaSelections[group.id] || []).length;
      return count < group.selection_min;
    });
    if (invalidGroup) {
      toast.error(`Complete la formule: ${invalidGroup.name}`);
      return;
    }
    const surchargeTotal = formulaDish.formula_groups.reduce((sum, group) => {
      return sum + getGroupSurchargeTotal(group);
    }, 0);
    addItem(
      formulaDish,
      1,
      [],
      'dine_in',
      {
        displayName: formulaDish.name,
        priceOverride: (formulaDish.prices?.dine_in || 0) + surchargeTotal,
        surchargeTotal,
        vatRate: formulaDish.vat_rate,
        formulaSelections: formulaDish.formula_groups.map(group => ({
          group_id: group.id,
          group_name: group.name,
          items: (formulaSelections[group.id] || []).map((item, index) => ({
            id: item.id,
            name: item.name,
            extra_price: getSelectionCharge(group, item, index),
          })),
        })),
      }
    );
    setFormulaDish(null);
    toast.success('Formule ajoutee !');
  };

  const handleOrder = async () => {
    if (!customerName.trim()) { toast.error('Veuillez indiquer votre nom'); return; }
    if (items.length === 0) { toast.error('Panier vide'); return; }
    if (settings?.dine_in_enabled === false) { toast.error('Le service sur place est temporairement indisponible'); return; }
    if (settings?.exceptional_closure_enabled) { toast.error(settings.exceptional_closure_message || 'Le restaurant est temporairement ferme'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/orders`, {
        type: 'dine_in',
        items: items.map(i => ({ dish_id: i.dish_id, name: i.name, quantity: i.quantity, price: i.price, supplements: i.supplements, formula_selections: i.formula_selections || [] })),
        customer: { name: customerName, phone: '', email: '', address: '' },
        table_id: table?.id || '', table_number: table?.number || 0,
        pickup_time: '', delivery_fee: 0,
      });
      clearCart();
      toast.success('Commande envoyee !');
      setMode('pay');
      loadOrders();
    } catch { toast.error('Erreur'); } finally { setLoading(false); }
  };

  const orderTotal = billSummary.remaining_total || existingOrders.reduce((s, o) => s + (o.total || 0), 0);
  const tipAmount = tipType === 'fixed' ? tip : Math.round(orderTotal * tip / 100 * 100) / 100;
  const perPersonSubtotal = splitCount > 1 ? Math.ceil(orderTotal / splitCount * 100) / 100 : orderTotal;
  const finalChargeAmount = perPersonSubtotal + tipAmount;

  const handlePay = async () => {
    if (existingOrders.length === 0) { toast.error('Aucune commande a payer'); return; }
    setLoading(true);
    try {
      const { data: session } = await axios.post(`${API}/api/payments/create-session`, {
        table_id: table?.id,
        qr_code_id: qrCodeId,
        bill_amount: perPersonSubtotal,
        tip_amount: tipAmount,
        split_count: splitCount,
        origin_url: window.location.origin,
      });
      if (session.url) { window.location.href = session.url; }
    } catch { toast.error('Erreur de paiement'); }
    finally { setLoading(false); }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]"><p className="text-[#8C3A3A] text-lg">{error}</p></div>;
  if (!table) return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]"><p className="text-[#7A7A75]">Chargement...</p></div>;

  return (
    <div className="min-h-screen bg-[#FAF8F5] pb-24" data-testid="table-order-page">
      <div className="bg-[#5B6B3F] text-white px-4 py-3 text-center">
        <p className="font-heading text-lg font-semibold">Table {table.number}</p>
        <p className="text-xs text-white/70">Le Traiteur Libanais</p>
      </div>
      {settings?.exceptional_closure_enabled && (
        <div className="mx-4 mt-4 rounded-2xl border border-[#8C3A3A]/20 bg-[#8C3A3A]/8 px-4 py-3 text-sm text-[#8C3A3A]">
          {settings.exceptional_closure_message}
        </div>
      )}

      {/* Mode Toggle */}
      <div className="flex bg-white border-b border-[#EBE8E0]">
        <button onClick={() => setMode('order')} className={`flex-1 py-3 text-sm font-medium text-center ${mode === 'order' ? 'text-[#5B6B3F] border-b-2 border-[#5B6B3F]' : 'text-[#7A7A75]'}`} data-testid="mode-order">
          <ShoppingBag className="w-4 h-4 inline mr-1" /> Commander
        </button>
        <button onClick={() => { setMode('pay'); loadOrders(); }} className={`flex-1 py-3 text-sm font-medium text-center ${mode === 'pay' ? 'text-[#5B6B3F] border-b-2 border-[#5B6B3F]' : 'text-[#7A7A75]'}`} data-testid="mode-pay">
          <Receipt className="w-4 h-4 inline mr-1" /> Payer l'addition
        </button>
      </div>

      {mode === 'order' ? (
        <>
          <div className="flex flex-wrap gap-2 px-4 py-3 bg-white border-b border-[#EBE8E0]">
            {visibleCategories.map(c => (
              <button key={c.id} onClick={() => setActiveCategory(c.id)} className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${activeCategory === c.id ? 'bg-[#5B6B3F] text-white' : 'bg-[#FAF8F5] text-[#7A7A75] hover:bg-[#F1EBDD] hover:text-[#5B6B3F]'}`}>{c.name}</button>
            ))}
          </div>
          <div className="px-4 py-4 space-y-3">
            {filtered.map(dish => (
              <div key={dish.id} className="bg-white rounded-xl border border-[#EBE8E0] p-3 flex gap-3" data-testid={`table-dish-${dish.id}`}>
                {dish.image && <img src={getPublicImageUrl(dish.image)} alt={dish.name} className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-sm text-[#2A2A28] truncate">{dish.name}</h3>
                  <p className="text-xs text-[#7A7A75] line-clamp-1">{dish.description}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-semibold text-[#5B6B3F]">{(dish.prices?.dine_in || 0).toFixed(2)} EUR</span>
                    <Button
                      size="sm"
                      disabled={!!settings?.exceptional_closure_enabled}
                      className="rounded-full bg-[#5B6B3F] hover:bg-[#4A5832] text-white h-8 px-3"
                      onClick={() => {
                        if (dish.dish_type === 'formula' && (dish.formula_groups || []).length > 0) {
                          openFormula(dish);
                          return;
                        }
                        addItem(dish, 1, [], 'dine_in');
                        toast.success('Ajoute !');
                      }}
                      data-testid={`table-add-${dish.id}`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  {dish.dish_type === 'formula' && <p className="mt-2 text-xs text-[#5B6B3F]">Prix sur place uniquement. Formule a composer.</p>}
                </div>
              </div>
            ))}
          </div>
          {/* Sticky Cart Bar */}
          {itemCount > 0 && (
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#EBE8E0] p-4 shadow-[0_-4px_20px_rgb(0,0,0,0.08)] z-50" data-testid="table-cart-bar">
              {showCart && (
                <div className="mb-4 max-h-60 overflow-y-auto space-y-2">
                  {items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between text-sm bg-[#FAF8F5] rounded-lg p-2">
                      <span className="truncate flex-1">{item.name}</span>
                      <div className="flex items-center gap-2">
                        <button onClick={() => updateQuantity(i, item.quantity - 1)}><Minus className="w-3.5 h-3.5" /></button>
                        <span className="font-medium">{item.quantity}</span>
                        <button onClick={() => updateQuantity(i, item.quantity + 1)}><Plus className="w-3.5 h-3.5" /></button>
                        <span className="ml-2 font-semibold">{(item.price * item.quantity).toFixed(2)} EUR</span>
                      </div>
                    </div>
                  ))}
                  <Input placeholder="Votre nom" value={customerName} onChange={e => setCustomerName(e.target.value)} className="mt-2 rounded-xl border-[#EBE8E0]" data-testid="table-customer-name" />
                </div>
              )}
              <div className="flex items-center gap-3">
                <button onClick={() => setShowCart(!showCart)} className="flex-1 flex items-center justify-between">
                  <span className="flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-[#5B6B3F]" /><span className="font-medium">{itemCount} article{itemCount > 1 ? 's' : ''}</span></span>
                  <span className="font-bold text-[#5B6B3F]">{total.toFixed(2)} EUR</span>
                </button>
                <Button onClick={handleOrder} disabled={loading} className="rounded-full bg-[#5B6B3F] hover:bg-[#4A5832] text-white px-6" data-testid="table-order-submit">
                  {loading ? '...' : 'Commander'}
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Payment Mode */
        <div className="px-4 py-6 space-y-4">
          {existingOrders.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="w-12 h-12 text-[#7A7A75] mx-auto mb-3" />
              <p className="text-[#7A7A75]">{billSummary.payment_complete ? 'Cette addition est deja reglee' : 'Aucune commande en cours pour cette table'}</p>
              <Button onClick={() => setMode('order')} className="mt-4 rounded-full bg-[#5B6B3F] hover:bg-[#4A5832] text-white">Commander</Button>
            </div>
          ) : (
            <>
              {/* Order Summary */}
              <div className="bg-white rounded-xl border border-[#EBE8E0] p-4">
                <h3 className="font-heading text-lg font-semibold text-[#2A2A28] mb-3">Votre addition</h3>
                {existingOrders.map(order => (
                  <div key={order.id} className="space-y-1 mb-3">
                    {order.items?.map((item, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span>{item.quantity}x {item.name}</span>
                        <span className="font-medium">{(item.price * item.quantity).toFixed(2)} EUR</span>
                      </div>
                    ))}
                  </div>
                ))}
                <div className="border-t border-[#EBE8E0] pt-2 flex justify-between font-semibold">
                  <span>Sous-total</span><span>{orderTotal.toFixed(2)} EUR</span>
                </div>
                {billSummary.paid_bill_amount > 0 && (
                  <div className="mt-2 flex justify-between text-sm text-[#5B6B3F]">
                    <span>Deja regle</span><span>{billSummary.paid_bill_amount.toFixed(2)} EUR</span>
                  </div>
                )}
              </div>

              {/* Tip Selection */}
              <div className="bg-white rounded-xl border border-[#EBE8E0] p-4">
                <h3 className="font-medium text-[#2A2A28] mb-3 flex items-center gap-2"><Heart className="w-4 h-4 text-[#8C3A3A]" /> Pourboire</h3>
                <div className="flex gap-2 mb-3">
                  <button onClick={() => setTipType('fixed')} className={`px-3 py-1 rounded-full text-xs ${tipType === 'fixed' ? 'bg-[#5B6B3F] text-white' : 'bg-[#FAF8F5] text-[#7A7A75]'}`}>Montant fixe</button>
                  <button onClick={() => setTipType('percent')} className={`px-3 py-1 rounded-full text-xs ${tipType === 'percent' ? 'bg-[#5B6B3F] text-white' : 'bg-[#FAF8F5] text-[#7A7A75]'}`}>Pourcentage</button>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={() => setTip(0)} className={`px-4 py-2 rounded-xl text-sm border ${tip === 0 ? 'bg-[#5B6B3F] text-white border-[#5B6B3F]' : 'border-[#EBE8E0] text-[#7A7A75]'}`} data-testid="tip-none">Pas de pourboire</button>
                  {(tipType === 'fixed' ? TIP_FIXED : TIP_PERCENT).map(v => (
                    <button key={v} onClick={() => setTip(v)} className={`px-4 py-2 rounded-xl text-sm border ${tip === v ? 'bg-[#D4AF37] text-white border-[#D4AF37]' : 'border-[#EBE8E0] text-[#7A7A75]'}`} data-testid={`tip-${v}`}>
                      {tipType === 'fixed' ? `${v} EUR` : `${v}%`}
                    </button>
                  ))}
                </div>
                {tipAmount > 0 && <p className="text-sm text-[#5B6B3F] mt-2">Pourboire : {tipAmount.toFixed(2)} EUR</p>}
              </div>

              {/* Split Bill */}
              <div className="bg-white rounded-xl border border-[#EBE8E0] p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-[#2A2A28] flex items-center gap-2"><Users className="w-4 h-4 text-[#5B6B3F]" /> Partager l'addition</h3>
                  <button onClick={() => setShowSplit(!showSplit)} className="text-sm text-[#5B6B3F] underline">{showSplit ? 'Annuler' : 'Partager'}</button>
                </div>
                {showSplit && (
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-[#7A7A75]">Nombre de personnes:</span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setSplitCount(Math.max(1, splitCount - 1))} className="w-8 h-8 rounded-full border border-[#EBE8E0] flex items-center justify-center"><Minus className="w-3.5 h-3.5" /></button>
                      <span className="font-bold text-lg w-8 text-center">{splitCount}</span>
                      <button onClick={() => setSplitCount(splitCount + 1)} className="w-8 h-8 rounded-full border border-[#EBE8E0] flex items-center justify-center"><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                )}
                {showSplit && splitCount > 1 && (
                  <p className="text-sm text-[#5B6B3F] mt-2 font-medium">Chacun paie : {perPersonSubtotal.toFixed(2)} EUR</p>
                )}
              </div>

              {/* Total & Pay */}
              <div className="bg-[#5B6B3F] rounded-xl p-4 text-white">
                <div className="flex justify-between mb-1 text-sm"><span>Sous-total</span><span>{orderTotal.toFixed(2)} EUR</span></div>
                {tipAmount > 0 && <div className="flex justify-between mb-1 text-sm"><span>Pourboire</span><span>{tipAmount.toFixed(2)} EUR</span></div>}
                <div className="flex justify-between font-bold text-lg border-t border-white/20 pt-2 mt-2">
                  <span>{showSplit && splitCount > 1 ? 'Votre part' : 'Addition'}</span>
                  <span>{perPersonSubtotal.toFixed(2)} EUR</span>
                </div>
                {tipAmount > 0 && (
                  <div className="mt-2 flex justify-between text-sm text-white/90">
                    <span>Montant a payer avec pourboire</span>
                    <span>{finalChargeAmount.toFixed(2)} EUR</span>
                  </div>
                )}
              </div>

              <Button onClick={handlePay} disabled={loading} className="w-full rounded-xl bg-[#D4AF37] hover:bg-[#c9a430] text-[#2A2A28] h-12 text-base font-semibold" data-testid="table-pay-btn">
                <CreditCard className="w-5 h-5 mr-2" /> {loading ? 'Traitement...' : `Payer ${finalChargeAmount.toFixed(2)} EUR`}
              </Button>
            </>
          )}
        </div>
      )}

      <Dialog open={!!formulaDish} onOpenChange={() => setFormulaDish(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{formulaDish?.name}</DialogTitle>
          </DialogHeader>
          {formulaDish && (
            <div className="space-y-6">
              {formulaDish.formula_groups.map(group => {
                const options = getDishesForGroup(group);
                return (
                  <div key={group.id} className="rounded-2xl border border-[#EBE8E0] p-4">
                    <h3 className="font-semibold text-[#2A2A28]">{group.name}</h3>
                    <p className="mb-3 text-xs text-[#7A7A75]">
                      {Number(group.selection_min || 0) > 0
                        ? `Choisis au moins ${group.selection_min} element(s)`
                        : 'Choix optionnel'}
                    </p>
                    <p className="mb-3 text-xs text-[#7A7A75]">
                      {getIncludedSelectionCount(group)} inclus dans la formule. Les choix supplementaires sont factures en plus.
                    </p>
                    <div className="grid gap-2">
                      {options.map(option => {
                        const choiceCount = getFormulaChoiceCount(group.id, option.id);
                        return (
                          <div
                            key={option.id}
                            className={`rounded-xl border px-4 py-3 transition ${
                              choiceCount > 0
                                ? 'border-[#5B6B3F] bg-[#5B6B3F]/8 text-[#2A2A28]'
                                : 'border-[#EBE8E0] bg-white text-[#2A2A28]'
                            }`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="font-medium">{option.name}</p>
                                {getOptionConfiguredExtra(group, option) > 0 ? (
                                  <p className="text-xs text-[#D4A840]">Inclus avec supplement +{getOptionConfiguredExtra(group, option).toFixed(2)} EUR</p>
                                ) : (
                                  <p className="text-xs text-[#7A7A75]">Inclus sans supplement</p>
                                )}
                                {getOptionExtraCount(group, option.id) > 0 && (
                                  <p className="text-xs text-[#D4A840]">
                                    +{Number(option.prices?.dine_in || 0).toFixed(2)} EUR x {getOptionExtraCount(group, option.id)} choix en plus
                                  </p>
                                )}
                                {choiceCount > 0 && <p className="text-xs text-[#5B6B3F]">Choisi {choiceCount} fois</p>}
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8 rounded-full border-[#EBE8E0]"
                                  onClick={() => removeFormulaChoice(group, option)}
                                  disabled={choiceCount === 0}
                                >
                                  <Minus className="w-3.5 h-3.5" />
                                </Button>
                                <Badge className="min-w-8 justify-center bg-[#F1EBDD] text-[#2A2A28] hover:bg-[#F1EBDD]">
                                  {choiceCount}
                                </Badge>
                                <Button
                                  type="button"
                                  size="icon"
                                  className="h-8 w-8 rounded-full bg-[#5B6B3F] hover:bg-[#4A5832] text-white"
                                  onClick={() => addFormulaChoice(group, option)}
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {options.length === 0 && (
                        <p className="text-sm text-[#8C3A3A]">
                          Aucun plat configure pour cette sous-categorie.
                        </p>
                      )}
                    </div>
                    {getGroupSurchargeTotal(group) > 0 && (
                      <p className="mt-3 text-xs font-medium text-[#5B6B3F]">
                        Supplement actuel pour {group.name}: +{getGroupSurchargeTotal(group).toFixed(2)} EUR
                      </p>
                    )}
                  </div>
                );
              })}
              <Button className="w-full rounded-xl bg-[#5B6B3F] hover:bg-[#4A5832] text-white" onClick={confirmFormula}>
                Ajouter la formule
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
